import React, { useState, useEffect } from 'react';
import { Share, PlusSquare, Download, X } from 'lucide-react';
import './InstallApp.css';

export const InstallApp = ({ isFloating = false }) => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [platform, setPlatform] = useState(''); // 'ios', 'android', 'other'
  const [showInstructions, setShowInstructions] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    // Check session storage for hidden state
    if (sessionStorage.getItem('installAppHidden') === 'true') {
      setIsVisible(false);
    }

    // Check if running as standalone PWA
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true) {
      setIsStandalone(true);
    }

    // Capture install prompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      console.log("Install prompt captured");
    };
    window.addEventListener('beforeinstallprompt', handler);

    // Detect Platform
    const ua = window.navigator.userAgent.toLowerCase();
    if (/iphone|ipad|ipod/.test(ua)) {
      setPlatform('ios');
    } else if (/android/.test(ua)) {
      setPlatform('android');
    } else {
      setPlatform('other'); // Laptop/Desktop falls here
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setDeferredPrompt(null);
      }
    } else {
      // Show manual instructions if no prompt available (iOS or Android fallback)
      setShowInstructions(true);
    }
  };

  const handleClose = (e) => {
    e.stopPropagation();
    setIsVisible(false);
    sessionStorage.setItem('installAppHidden', 'true');
  };

  // If already installed/standalone, hide button
  if (isStandalone) return null;

  // Show only on mobile devices (iOS or Android), regardless of install prompt availability
  const isMobile = platform === 'ios' || platform === 'android';
  if (!isMobile || !isVisible) return null;

  return (
    <>
      <button 
        className={isFloating ? "install-app-btn-floating" : "install-app-btn"} 
        onClick={handleInstallClick}
      >
        <Download size={18} style={{ marginRight: '8px' }} />
        Install App
        <div 
          onClick={handleClose}
          style={{ 
            marginLeft: '12px', 
            display: 'flex', 
            alignItems: 'center',
            padding: '4px',
            borderRadius: '50%',
            backgroundColor: 'rgba(255,255,255,0.2)'
          }}
        >
          <X size={14} />
        </div>
      </button>

      {showInstructions && (
        <div className="ios-instructions-overlay" onClick={() => setShowInstructions(false)}>
          <div className="ios-instructions-card" onClick={e => e.stopPropagation()}>
            <div className="ios-header">
              <h3>Install App</h3>
              <button className="close-btn" onClick={() => setShowInstructions(false)}>&times;</button>
            </div>
            
            {platform === 'ios' ? (
              <>
                <p>Install on your iPhone for the best experience:</p>
                <ol>
                  <li>Tap the <strong>Share</strong> button <Share size={16} style={{ verticalAlign: 'middle', display: 'inline' }} /> below.</li>
                  <li>Scroll down and tap <strong>Add to Home Screen</strong> <PlusSquare size={16} style={{ verticalAlign: 'middle', display: 'inline' }} />.</li>
                </ol>
              </>
            ) : (
              <>
                <p>To install the app:</p>
                <ol>
                  <li>Tap the browser menu <span style={{fontSize: '18px', fontWeight: 'bold'}}>⋮</span> (three dots).</li>
                  <li>Select <strong>Install App</strong> or <strong>Add to Home screen</strong>.</li>
                </ol>
              </>
            )}
            
            <button className="got-it-btn" onClick={() => setShowInstructions(false)}>Got it</button>
          </div>
        </div>
      )}
    </>
  );
};
